// Learner profile selection and management.
import { SUPABASE_KEY, SUPABASE_URL } from "../config.js";
import { loadAccount, loadExercises } from "../data/account.js";
import { loadAdminStatistics } from "../data/admin.js";
import {
  avatarLabel,
  avatarOptions,
  avatarPicture,
  pawnStorageKey,
  profileSchoolLabel
} from "../domain/avatars.js";
import { bindLanguage, copy, languageControl, t } from "../i18n.js";
import { app, closeModal, escapeHTML, setBusy, showModal, showToast } from "../lib/dom.js";
import { logout, render } from "../router.js";
import { state } from "../state.js";
import { supabase } from "../supabase-client.js";
import { parentView } from "./parent.js";

export function profileSelection() {
  if (state.roles.includes("parent")) return parentView();
  app.innerHTML = `
    <main class="main-content" style="margin-left:0;max-width:1000px;margin-inline:auto">
      <div class="topbar">
        <div><h1>${t("profiles")}</h1><p>${t("profilesHelp")}</p></div>
        <div>${languageControl()}</div>
      </div>
      <div class="profile-grid">
        ${state.profiles.map(profile => `
          <div class="profile-card">
            <button class="ghost full" data-profile="${profile.id}" style="padding:0;text-align:left">
              <span class="avatar">${avatarPicture(profile.id)}</span>
              <h3>${escapeHTML(profile.nickname)}</h3>
              <p>${escapeHTML(profile.age_band)} · ${t("level")} ${profile.level}</p>
              <p>${escapeHTML(profileSchoolLabel(profile.id))}</p>
            </button>
            ${state.roles.includes("parent") ? `<button class="link-button" data-child-password="${profile.id}" style="margin-top:8px">${state.language === "it" ? "Modifica password del figlio" : "Change child password"}</button>` : ""}
            ${state.roles.includes("parent") ? `<button class="link-button" data-change-class="${profile.id}" style="margin-top:8px">${state.language === "it" ? "Modifica scuola o classe" : "Change school or class"}</button>` : ""}
            ${state.roles.includes("learner") ? `<button class="link-button" data-create-guardian-link="${profile.id}" style="margin-top:8px">${state.language === "it" ? "Invita un genitore a vedere i progressi" : "Invite a parent to view progress"}</button>` : ""}
          </div>`).join("")}
        ${state.roles.includes("parent") ? `<button class="profile-card" id="add-profile">
          <span class="avatar">＋</span>
          <h3>${state.roles.includes("parent") ? t("addProfile") : (state.language === "it" ? "Crea il mio profilo studente" : "Create my learner profile")}</h3>
          <p>${state.language === "it" ? "Livello e XP partiranno da zero." : "Level and XP will start from zero."}</p>
        </button>` : ""}
      </div>
      <div style="margin-top:25px"><button class="ghost" id="profile-logout">${t("logout")}</button></div>
      ${state.roles.includes("learner") ? `<div class="card" style="margin-top:18px">
        <strong>${state.language === "it" ? "Hai già un profilo gestito da un genitore?" : "Already have a profile managed by a parent?"}</strong>
        <p style="color:var(--muted)">${state.language === "it" ? "Collegalo usando il link ricevuto dal genitore." : "Connect it using the link received from the parent."}</p>
        <button class="secondary" id="link-existing-profile">${state.language === "it" ? "Collega profilo esistente" : "Link existing profile"}</button>
      </div>` : ""}
      ${state.roles.includes("teacher") ? `<button class="primary full" id="continue-teacher" style="margin-top:18px">${state.language === "it" ? "Continua come insegnante" : "Continue as teacher"}</button>` : ""}
      ${state.isAdmin ? `<button class="secondary full" id="continue-admin" style="margin-top:12px">${state.language === "it" ? "Area amministratore" : "Administrator area"}</button>` : ""}
    </main>`;
  bindLanguage();
  document.querySelectorAll("[data-profile]").forEach(button => {
    button.addEventListener("click", async () => {
      state.profile = state.profiles.find(profile => profile.id === button.dataset.profile);
      localStorage.setItem("brainmath-profile", state.profile.id);
      await loadExercises();
      if (state.pendingClassToken) {
        const { error } = await supabase.rpc("redeem_invitation", {
          raw_token: state.pendingClassToken,
          selected_child_profile: state.profile.id
        });
        if (error) {
          showToast(error.message);
        } else {
          state.pendingClassToken = null;
          history.replaceState({}, "", "/");
          showToast(state.language === "it" ? "Richiesta di accesso alla classe inviata." : "Class access request sent.");
        }
      }
      state.view = "home";
      render();
    });
  });
  document.querySelector("#add-profile")?.addEventListener("click", addProfileModal);
  document.querySelector("#continue-admin")?.addEventListener("click", async () => {
    state.adminMode = true;
    state.teacherMode = false;
    state.profile = null;
    await loadAdminStatistics();
    render();
  });
  document.querySelectorAll("[data-create-guardian-link]").forEach(button => {
    button.addEventListener("click", async () => {
      button.disabled = true;
      const { data, error } = await supabase.rpc("create_guardian_profile_link_invitation", {
        selected_child_profile: button.dataset.createGuardianLink
      });
      button.disabled = false;
      if (error) return showToast(error.message);
      const url = `${location.origin}/profile/${data}`;
      showModal(`
        <div class="modal-header"><h2>${state.language === "it" ? "Invito genitore" : "Parent invitation"}</h2><button class="close-button" data-close-modal>×</button></div>
        <p style="color:var(--muted)">${state.language === "it" ? "Il genitore deve aprire questo link con il proprio account BrainMath." : "The parent should open this link using their BrainMath account."}</p>
        <input value="${escapeHTML(url)}" readonly>
        <button class="primary full" id="copy-guardian-link" style="margin-top:12px">${state.language === "it" ? "Copia link" : "Copy link"}</button>`);
      document.querySelector("#copy-guardian-link").addEventListener("click", async () => {
        await navigator.clipboard.writeText(url);
        showToast(state.language === "it" ? "Link copiato." : "Link copied.");
      });
    });
  });
  document.querySelectorAll("[data-child-password]").forEach(button => {
    button.addEventListener("click", () => childPasswordModal(button.dataset.childPassword));
  });
  document.querySelectorAll("[data-change-class]").forEach(button => {
    button.addEventListener("click", () => changeSchoolClassModal(button.dataset.changeClass));
  });
  document.querySelector("#link-existing-profile")?.addEventListener("click", profileLinkModal);
  document.querySelector("#continue-teacher")?.addEventListener("click", () => {
    state.teacherMode = true;
    state.view = "home";
    render();
  });
  document.querySelector("#profile-logout").addEventListener("click", logout);
  if (state.pendingProfileToken && (state.roles.includes("learner") || state.roles.includes("parent"))) {
    redeemPendingProfileLink();
  }
}

export function addProfileModal() {
  const schoolOptions = [...new Map(
    state.schoolClasses.map(item => [item.school_id, {
      id: item.school_id,
      name: item.school_name,
      city: item.school_city
    }])
  ).values()];
  showModal(`
    <div class="modal-header"><h2>${state.roles.includes("parent") ? t("addProfile") : (state.language === "it" ? "Profilo studente" : "Learner profile")}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="profile-form" class="form-grid">
      <label>${t("nickname")}<input name="nickname" minlength="3" maxlength="24" required></label>
      <label>${state.language === "it" ? "Tipo profilo" : "Profile type"}<select name="profileType" required>
        <option value="male">${state.language === "it" ? "Maschio" : "Boy"}</option>
        <option value="female">${state.language === "it" ? "Femmina" : "Girl"}</option>
      </select></label>
      <div class="profile-avatar-create full">
        <div><span class="eyebrow">${state.language === "it" ? "Avatar del profilo" : "Profile avatar"}</span><h3>${state.language === "it" ? "Scegli il suo personaggio" : "Choose their character"}</h3><p>${state.language === "it" ? "Gli avatar mostrati dipendono dal tipo di profilo scelto." : "The available avatars depend on the selected profile type."}</p></div>
        <input name="avatarSymbol" type="hidden" value="boy-explorer">
        <div class="avatar-options profile-avatar-create-options" id="profile-avatar-create-options"></div>
      </div>
      <label>${state.language === "it" ? "Username del figlio" : "Child username"}<input name="username" pattern="[A-Za-z0-9_]{3,24}" autocomplete="off" required></label>
      <label>${state.language === "it" ? "Password del figlio" : "Child password"}<input name="password" type="password" minlength="8" autocomplete="new-password" required></label>
      <label>${t("birthDate")}<input name="birthDate" type="date" required></label>
      <label>${state.language === "it" ? "Scuola" : "School"}<select name="schoolID" required>
        <option value="">${state.language === "it" ? "Seleziona scuola" : "Select school"}</option>
        ${schoolOptions.map(school => `<option value="${school.id}">${escapeHTML(school.name)} — ${escapeHTML(school.city)}</option>`).join("")}
      </select></label>
      <label>${state.language === "it" ? "Classe" : "Class"}<select name="classID" required disabled>
        <option value="">${state.language === "it" ? "Seleziona prima la scuola" : "Select a school first"}</option>
      </select></label>
      <button class="primary full" type="submit">${t("createProfile")}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#profile-form");
  const schoolSelect = form.elements.schoolID;
  const classSelect = form.elements.classID;
  const profileTypeSelect = form.elements.profileType;
  const avatarSymbolInput = form.elements.avatarSymbol;
  const avatarCreateOptions = form.querySelector("#profile-avatar-create-options");
  const renderCreateAvatarOptions = () => {
    const options = avatarOptions(profileTypeSelect.value);
    if (!options.some(option => option.code === avatarSymbolInput.value)) avatarSymbolInput.value = options[0].code;
    avatarCreateOptions.innerHTML = options.map(avatar => `<button type="button" class="avatar-option ${avatar.code === avatarSymbolInput.value ? "selected" : ""}" data-create-avatar="${avatar.code}" title="${escapeHTML(avatarLabel(avatar))}"><img src="${avatar.image}" alt=""><small>${escapeHTML(avatarLabel(avatar))}</small></button>`).join("");
    avatarCreateOptions.querySelectorAll("[data-create-avatar]").forEach(button => {
      button.addEventListener("click", () => {
        avatarSymbolInput.value = button.dataset.createAvatar;
        renderCreateAvatarOptions();
      });
    });
  };
  profileTypeSelect.addEventListener("change", () => {
    avatarSymbolInput.value = avatarOptions(profileTypeSelect.value)[0].code;
    renderCreateAvatarOptions();
  });
  renderCreateAvatarOptions();
  schoolSelect.addEventListener("change", () => {
    const classes = state.schoolClasses.filter(item => item.school_id === schoolSelect.value);
    classSelect.innerHTML = `<option value="">${state.language === "it" ? "Seleziona classe" : "Select class"}</option>` +
      classes.map(item => `<option value="${item.class_id}">${escapeHTML(item.class_name)} · ${escapeHTML(item.teacher_name)}</option>`).join("");
    classSelect.disabled = classes.length === 0;
  });
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const button = form.querySelector("button[type=submit]");
    setBusy(button);
    const values = new FormData(form);
    const { data: sessionData } = await supabase.auth.getSession();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/create-child-account`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${sessionData.session.access_token}`
      },
      body: JSON.stringify({
        nickname: values.get("nickname").trim(),
        username: values.get("username").trim(),
        password: values.get("password"),
        birth_date: values.get("birthDate"),
        profile_type: values.get("profileType"),
        avatar_symbol: values.get("avatarSymbol"),
        class_id: values.get("classID")
      })
    });
    const result = await response.json();
    if (!response.ok) {
      form.querySelector(".form-message").textContent = result.message;
      setBusy(button, false);
      return;
    }
    await loadAccount();
    const createdProfileID = result.profile?.id || state.profiles.find(profile => profile.username === values.get("username").trim())?.id;
    if (createdProfileID) localStorage.removeItem(pawnStorageKey(createdProfileID));
    closeModal();
    profileSelection();
  });
}

export function childPasswordModal(childProfileID) {
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Nuova password figlio" : "New child password"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="child-password-form" class="form-grid">
      <label>${t("password")}<input name="password" type="password" minlength="8" required></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Aggiorna password" : "Update password"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#child-password-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const { data: sessionData } = await supabase.auth.getSession();
    const response = await fetch(`${SUPABASE_URL}/functions/v1/update-child-password`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        apikey: SUPABASE_KEY,
        Authorization: `Bearer ${sessionData.session.access_token}`
      },
      body: JSON.stringify({
        child_profile_id: childProfileID,
        password: new FormData(form).get("password")
      })
    });
    const result = await response.json();
    if (!response.ok) {
      form.querySelector(".form-message").textContent = result.message;
      return;
    }
    closeModal();
    showToast(state.language === "it" ? "Password aggiornata." : "Password updated.");
  });
}

export function changeSchoolClassModal(childProfileID) {
  const schoolOptions = [...new Map(
    state.schoolClasses.map(item => [item.school_id, {
      id: item.school_id,
      name: item.school_name,
      city: item.school_city
    }])
  ).values()];
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Modifica scuola o classe" : "Change school or class"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="change-class-form" class="form-grid">
      <label>${state.language === "it" ? "Scuola" : "School"}<select name="schoolID" required>
        <option value="">${state.language === "it" ? "Seleziona scuola" : "Select school"}</option>
        ${schoolOptions.map(school => `<option value="${school.id}">${escapeHTML(school.name)} — ${escapeHTML(school.city)}</option>`).join("")}
      </select></label>
      <label>${state.language === "it" ? "Classe" : "Class"}<select name="classID" required disabled></select></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Salva" : "Save"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#change-class-form");
  const schoolSelect = form.elements.schoolID;
  const classSelect = form.elements.classID;
  schoolSelect.addEventListener("change", () => {
    const classes = state.schoolClasses.filter(item => item.school_id === schoolSelect.value);
    classSelect.innerHTML = classes.map(item => `<option value="${item.class_id}">${escapeHTML(item.class_name)} · ${escapeHTML(item.teacher_name)}</option>`).join("");
    classSelect.disabled = classes.length === 0;
  });
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const { error } = await supabase.rpc("update_student_school_class", {
      selected_child_profile: childProfileID,
      selected_class: classSelect.value
    });
    if (error) {
      form.querySelector(".form-message").textContent = error.message;
      return;
    }
    await loadAccount();
    closeModal();
    profileSelection();
  });
}

function profileLinkModal() {
  showModal(`
    <div class="modal-header"><h2>${state.language === "it" ? "Collega profilo" : "Link profile"}</h2><button class="close-button" data-close-modal>×</button></div>
    <form id="profile-link-form" class="form-grid">
      <label>${state.language === "it" ? "Link o codice ricevuto" : "Received link or code"}<input name="token" required></label>
      <button class="primary full" type="submit">${state.language === "it" ? "Collega" : "Link"}</button>
      <p class="form-message"></p>
    </form>`);
  const form = document.querySelector("#profile-link-form");
  form.addEventListener("submit", async event => {
    event.preventDefault();
    const raw = new FormData(form).get("token").trim();
    state.pendingProfileToken = raw.split("/").filter(Boolean).pop();
    await redeemPendingProfileLink();
  });
}

async function redeemPendingProfileLink() {
  if (!state.pendingProfileToken) return;
  const { error } = await supabase.rpc("redeem_child_profile_link", {
    raw_token: state.pendingProfileToken
  });
  if (error) {
    showToast(error.message);
    return;
  }
  state.pendingProfileToken = null;
  history.replaceState({}, "", "/");
  closeModal();
  await loadAccount();
  showToast(state.language === "it" ? "Profilo collegato al login dello studente." : "Profile linked to the learner login.");
  profileSelection();
}
